// @flow
import type { Dispatch } from 'react-redux';
import { finectAPI } from '@finect/front-utils/finectAPI';
import { BANKS_HIGHLIGHTS_TAG_ID } from '../../configs';
import {
  BANKS_SET_ORGANIZATION,
  BANKS_SET_ORGANIZATION_FETCHING_STATUS,
  BANKS_SET_ORGANIZATION_ERROR,
  BANKS_SET_ARTICLE,
  BANKS_SET_ARTICLE_FETCHING_STATUS,
  BANKS_SET_QUESTIONS,
  BANKS_SET_QUESTIONS_FETCHING_STATUS,
  BANKS_SET_MANAGEMENT_COMPANIES,
  BANKS_SET_MANAGEMENT_COMPANIES_FETCHING_STATUS,
  BANKS_CLEAR,
  BANKS_SET_PAGE,
  BANKS_SET_PAGE_FETCHING_STATUS
} from '../constants';

import { getOrganizationBankWithCMS } from '../cms/utils';
import { getBanks } from '../content/actions';

import type { BanksGetQuestionsActionType, BanksGetManagementCompaniesActionType } from './types';

function setPage(page: Object) {
  return {
    type: BANKS_SET_PAGE,
    page
  };
}

function setPageFetchingStatus(fetching: boolean, fetched: boolean) {
  return {
    type: BANKS_SET_PAGE_FETCHING_STATUS,
    fetching,
    fetched
  };
}

export function setBankOrganization(organization: Object) {
  return {
    type: BANKS_SET_ORGANIZATION,
    organization
  };
}

export function setBankOrganizationFetchingStatus(fetching: boolean, fetched: boolean) {
  return {
    type: BANKS_SET_ORGANIZATION_FETCHING_STATUS,
    fetching,
    fetched
  };
}

/**
 * Marca la carga del banco como fallida guardando el status HTTP real (404 vs 5xx),
 * para que las escenas puedan responder 404 o 503 en vez de un 200 en blanco.
 */
export function setBankOrganizationError(status: number) {
  return {
    type: BANKS_SET_ORGANIZATION_ERROR,
    status
  };
}

// Article
export function setBankArticle(article: Object) {
  return {
    type: BANKS_SET_ARTICLE,
    article
  };
}

export function setBankArticleFetchingStatus(fetching: boolean, fetched: boolean) {
  return {
    type: BANKS_SET_ARTICLE_FETCHING_STATUS,
    fetching,
    fetched
  };
}

export function fetchBankArticle(cookie: string, alias: string, force: boolean = true) {
  return (dispatch: Dispatch, getState: Function) => {
    const fetched = getState().banks.article.fetched;

    if (force || !fetched) {
      return Promise.all([dispatch(setBankArticleFetchingStatus(true, false))]).then(() =>
        finectAPI({
          credentials: cookie,
          version: 'v4',
          path: `contents/pages/${alias}`,
          method: 'GET',
          onSuccess: ({ data }) => dispatch(setBankArticle(data)),
          onError: () => dispatch(setBankArticleFetchingStatus(false, true))
        }));
    }

    return Promise.all([]);
  };
}

// Questions
export function setBankQuestions(request: BanksGetQuestionsActionType, questions: Array<Object>) {
  return {
    type: BANKS_SET_QUESTIONS,
    request,
    questions
  };
}

export function fetchBankQuestionsFetchingStatus(
  request: BanksGetQuestionsActionType,
  fetching: boolean,
  fetched: boolean
) {
  return {
    type: BANKS_SET_QUESTIONS_FETCHING_STATUS,
    request,
    fetching,
    fetched
  };
}

export function fetchBankQuestions(request: BanksGetQuestionsActionType, force: boolean = true) {
  const { organization, limit = 5 } = request;

  const requestId: BanksGetQuestionsActionType = { ...request, ...{ organization, limit } };

  return (dispatch: Dispatch, getState: Function) => {
    const fetched = getState().banks.questions.fetched;

    return force || !fetched
      ? Promise.all([dispatch(fetchBankQuestionsFetchingStatus(requestId, true, true))]).then(() =>
        finectAPI({
          version: 'v3',
          path: `questions?group=${organization}&limit=${limit}`,
          method: 'GET',
          onSuccess: ({ list }: { list: Object }) => Promise.all([dispatch(setBankQuestions(requestId, list))]),
          // Sin onError finectAPI relanza y el rechazo sube hasta el SSR: 500 de toda la ficha
          onError: () => Promise.all([dispatch(fetchBankQuestionsFetchingStatus(requestId, false, true))])
        }))
      : Promise.all([]);
  };
}

// Management Companies
export function setBankManagementCompanies(
  request: BanksGetManagementCompaniesActionType,
  managementCompanies: Array<Object>
) {
  return {
    type: BANKS_SET_MANAGEMENT_COMPANIES,
    request,
    managementCompanies
  };
}

export function fetchBankManagementCompaniesFetchingStatus(
  request: BanksGetManagementCompaniesActionType,
  fetching: boolean,
  fetched: boolean
) {
  return {
    type: BANKS_SET_MANAGEMENT_COMPANIES_FETCHING_STATUS,
    request,
    fetching,
    fetched
  };
}

export function clearBank() {
  return {
    type: BANKS_CLEAR
  };
}

export function clearBankScenes() {
  return function (dispatch: Dispatch) {
    return Promise.all([dispatch(clearBank())]);
  };
}

export function fetchBankManagementCompanies(request: BanksGetManagementCompaniesActionType, force: boolean = true) {
  const { organization, limit } = request;

  return (dispatch: Dispatch, getState: Function) => {
    const fetched = getState().banks.managementCompanies.fetched;

    if (force || !fetched) {
      return Promise.all([dispatch(fetchBankManagementCompaniesFetchingStatus(request, true, true))]).then(() =>
        finectAPI({
          version: 'v4',
          path: `contents/distributors/${organization}/managementcompanies?limit=${limit}`,
          method: 'GET',
          onSuccess: ({ data }: { data: Object }) => Promise.all([dispatch(setBankManagementCompanies(request, data))]),
          // Sin onError finectAPI relanza y el rechazo sube hasta el SSR: 500 de toda la ficha
          onError: () => Promise.all([dispatch(fetchBankManagementCompaniesFetchingStatus(request, false, true))])
        }));
    }

    return Promise.all([]);
  };
}

function fetchBankWithCMS(cookies, organizationAlias) {
  return function (dispatch: Dispatch) {
    return (
      getOrganizationBankWithCMS(cookies, organizationAlias)
        .then((result) => {
          if (!result || result.error) return dispatch(setBankOrganizationError((result && result.error) || 502));

          const { page, organization } = result;
          return Promise.all([dispatch(setPage(page)), dispatch(setBankOrganization(organization))]);
        })
        // Los errores sin status (red, DNS, timeout) los relanza finectAPI, no pasan por onError
        .catch(error => dispatch(setBankOrganizationError((error && error.status) || 502)))
    );
  };
}

// SSR - loadInitialData
export function fetchBankArticleScene(cookies: string, organization: string, article: string) {
  return function (dispatch: Dispatch) {
    return Promise.all([
      dispatch(setBankOrganizationFetchingStatus(true, false)),
      dispatch(setPageFetchingStatus(true, false))
    ]).then(() =>
      Promise.all([
        dispatch(fetchBankWithCMS(cookies, organization)),
        dispatch(fetchBankArticle(cookies, article, true))
      ]));
  };
}

export function fetchBankHomeScene(
  cookies: string,
  force: boolean,
  organization: string,
  questionsRequest: BanksGetQuestionsActionType
) {
  return function (dispatch: Dispatch) {
    return Promise.all([
      // fetched sólo pasa a true cuando el intento termina; marcarlo aquí hacía que
      // "cargando" y "ya cargado" fueran indistinguibles para las escenas
      dispatch(setBankOrganizationFetchingStatus(true, false)),
      dispatch(setPageFetchingStatus(true, false))
    ]).then(() =>
      Promise.all([
        dispatch(fetchBankWithCMS(cookies, organization)),
        dispatch(getBanks(BANKS_HIGHLIGHTS_TAG_ID)),
        dispatch(fetchBankQuestions(questionsRequest, true)),
        dispatch(fetchBankManagementCompanies({ organization, limit: 50 }))
      ]));
  };
}

export function fetchBankProductsScene(cookies: string, organization: string) {
  return function (dispatch: Dispatch) {
    return Promise.all([
      dispatch(setBankOrganizationFetchingStatus(true, false)),
      dispatch(setPageFetchingStatus(true, false))
    ]).then(() => dispatch(fetchBankWithCMS(cookies, organization)));
  };
}
